import React, { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
// @ts-ignore
import hamburgerIcon from "./../../images/homePage/hamburger-ic.svg";
import MaxWidthWrapper from "../common/MaxWidthWrapper";
import SecondaryWidthWrapper from "../common/SecondaryWidthWrapper";
import NavbarType from "../../types/Navbar.type";
import { Link } from "gatsby";
import PageRedirectionHandler from "../../helper/handler/PageRedirection.handler";

//@ts-ignore
import arrow from "../../images/nav/arrow.svg";

interface NavbarProps {
  navbarData: NavbarType;
  open: boolean;
  setOpen(status: boolean): void;
}

const NavBar: React.FC<NavbarProps> = ({ navbarData, open, setOpen }) => {
  if (!navbarData || !navbarData.pagesData) {
    return null;
  }

  const [y, setY] = useState<number>(
    typeof window !== "undefined" ? window.scrollY : 0
  );
  const [scrollFlag, setScrollFlag] = useState<boolean>(false);

  const handleNavigation = useCallback(() => {
    const scrollY = window.scrollY;

    if (scrollY < 100) {
      setScrollFlag(false); // always show navbar at top
    } else {
      setScrollFlag(scrollY < y); // show on scroll up
    }

    setY(scrollY);
  }, [y]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleNavigation);
      return () => window.removeEventListener("scroll", handleNavigation);
    }
  }, [handleNavigation]);

  const activePageHandler = (pageUrl: string) => {
    return PageRedirectionHandler(pageUrl);
  };

  const groupedNavItems = () => {
    if (!navbarData?.pagesData || !Array.isArray(navbarData.pagesData))
      return [];

    const parentsMap: Record<string, any> = {};
    const childPages: any[] = [];

    navbarData.pagesData.forEach((page) => {
      if (page.parent) {
        childPages.push(page);
      } else {
        parentsMap[page.url] = { ...page, children: [] };
      }
    });

    childPages.forEach((child) => {
      const parentUrl = child.parent;
      const matchedParent = Object.keys(parentsMap).find(
        (url) => url === parentUrl
      );

      if (matchedParent) {
        parentsMap[matchedParent].children.push(child);
      } else {
        if (!parentsMap[parentUrl]) {
          parentsMap[parentUrl] = {
            name:
              parentUrl.replace(/\//g, "").replace(/-/g, " ").trim() || "Other",
            url: "#",
            children: [],
            isSynthetiParent: true,
          };
        }
        parentsMap[parentUrl].children.push(child);

        // 🆕 Set order from first child's order (if not already set)
        if (!parentsMap[parentUrl].order) {
          parentsMap[parentUrl].order = child.order ?? 999;
        }
      }
    });

    const navItems = Object.values(parentsMap).sort(
      (a: any, b: any) => (a.order ?? 999) - (b.order ?? 999)
    );

    return navItems;
  };

  const navItems = groupedNavItems();

  return (
    <nav
      className={classNames(
        "relative sticky top-0 z-[1000] flex items-start bg-[#F5FDF6] transition-all duration-300 ease-in-out",
        {
          "shadow-sm": y > 0,
          "pointer-events-none translate-y-[-100%] opacity-0": scrollFlag,
          "translate-y-0 opacity-100": !scrollFlag,
        }
      )}
    >
      <MaxWidthWrapper>
        <SecondaryWidthWrapper>
          <div className="flex flex-col-reverse md:flex-row">
            <div className="flex w-full items-center justify-between py-[16px]">
              <Link to={navbarData?.companyLogo?.url || "/"}>
                {/* @ts-ignore  */}
                <img
                  className="h-[40px] lg:h-[50px]"
                  src={navbarData?.companyLogo?.media}
                  alt={navbarData?.companyLogo?.altText}
                />
              </Link>
              <div className="hidden items-center justify-between lg:flex">
                <div className="flex w-full items-center gap-[40px] text-black-400">
                  {navItems.map((pageData, index) => {
                    const hasChildren =
                      pageData.children && pageData.children.length > 0;
                    const isActiveParent =
                      typeof window !== "undefined" &&
                      (hasChildren
                        ? pageData.children.some((child: any) =>
                            activePageHandler(child.url)
                          )
                        : activePageHandler(pageData.url));

                    return (
                      <div key={index} className="group relative">
                        <p
                          className={classNames(
                            "shrink-0 whitespace-nowrap font-medium",
                            {
                              "font-semibold text-primary": isActiveParent,
                              "cursor-pointer": hasChildren,
                            }
                          )}
                        >
                          {hasChildren ? (
                            <div className="group flex items-center gap-1">
                              <span className=" capitalize">
                                {pageData.name}
                              </span>{" "}
                              <img
                                src={arrow}
                                alt="arrow"
                                className="h-5 w-5 transform transition-transform duration-300 group-hover:rotate-180"
                              />
                            </div>
                          ) : (
                            <Link to={pageData.url}>{pageData?.name}</Link>
                          )}
                        </p>

                        {hasChildren && (
                          <div
                            style={{ boxShadow: "0px 4px 4px 0px #92E3A970" }}
                            className="absolute left-0 top-full z-50 hidden min-w-max flex-col rounded-md bg-white p-2 shadow-lg group-hover:flex"
                          >
                            {pageData.children.map(
                              (child: any, idx: number) => {
                                const isActiveChild = activePageHandler(
                                  child.url
                                );
                                const icon =
                                  child?.navbarIcon?.media?.data?.[0]
                                    ?.attributes?.url;

                                return (
                                  <Link
                                    key={idx}
                                    to={child.url}
                                    className={classNames(
                                      "flex items-center gap-4 whitespace-nowrap px-4 py-2 text-[16px] text-sm font-medium hover:bg-gray-100",
                                      {
                                        "font-semibold text-primary":
                                          isActiveChild,
                                      }
                                    )}
                                  >
                                    {icon && (
                                      <img
                                        src={icon}
                                        alt={child.name}
                                        className="h-8 w-8"
                                      />
                                    )}
                                    {child.name}
                                  </Link>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <Link
                    className="w-full cursor-pointer rounded !bg-primary py-[6px] px-[12px] font-medium text-white drop-shadow-sm sm:bg-hero-gradient-start"
                    to={
                      typeof window !== "undefined" &&
                      window.location.pathname === "/"
                        ? "#contact-us"
                        : "/#contact-us"
                    }
                  >
                    {navbarData?.buttonData?.text}
                  </Link>
                </div>
              </div>
              <div
                className="block flex h-[43px] w-[43px] cursor-pointer items-center justify-center lg:hidden"
                onClick={() => setOpen(!open)}
              >
                <button type="button">
                  <img
                    alt={"ICON"}
                    src={hamburgerIcon}
                    height={43}
                    width={43}
                  />
                </button>
              </div>
            </div>
          </div>
        </SecondaryWidthWrapper>
      </MaxWidthWrapper>
    </nav>
  );
};

export default NavBar;
